import { useCallback, useEffect, useRef, useState } from 'react';
import { EditorItem, useEditorStore } from '../store/editorStore';

export type ResizeDirection = 'nw' | 'ne' | 'sw' | 'se' | 'n' | 'e' | 's' | 'w';

// Màu xanh dương Mantine giống với buttons
export const HANDLE_COLOR = '#228be6';

type UseDraggableItemProps = {
  item: EditorItem;
  isSelected: boolean;
  scale: number;
  canvasWidth?: number;
  canvasHeight?: number;
  itemRef: React.RefObject<HTMLDivElement>;
  onSelect: () => void;
};

export function useDraggableItem({
  item,
  isSelected,
  scale,
  canvasWidth,
  canvasHeight,
  itemRef,
  onSelect
}: UseDraggableItemProps) {
  const selectItem = useEditorStore((state) => state.selectItem);
  const updateItem = useEditorStore((state) => state.updateItem);
  const deleteItem = useEditorStore((state) => state.deleteItem);
  const addItem = useEditorStore((state) => state.addItem);
  
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(item.content);
  
  const startPosRef = useRef({ mouseX: 0, mouseY: 0, itemX: 0, itemY: 0 });
  const startSizeRef = useRef({ width: 0, height: 0, x: 0, y: 0 });
  const resizingRef = useRef<ResizeDirection | null>(null);
  
  // Sử dụng scale để chuyển đổi giữa tọa độ thực và tọa độ hiển thị
  const scaledX = item.position.x * scale;
  const scaledY = item.position.y * scale;
  const scaledWidth = item.size.width * scale;
  const scaledHeight = item.size.height * scale;

  // Apply the position and size with scaled values
  const itemStyle: React.CSSProperties = {
    position: 'absolute',
    left: `${scaledX}px`,
    top: `${scaledY}px`,
    width: `${scaledWidth}px`,
    height: `${scaledHeight}px`,
    zIndex: isSelected ? 10 : 1,
    border: isSelected ? `2px solid ${HANDLE_COLOR}` : 'none',
    boxSizing: 'border-box',
    cursor: isEditing ? 'text' : 'move',
    overflow: 'hidden',
    backgroundColor: (item.style as any)?.backgroundColor,
    userSelect: 'none',
  };

  // Handle keyboard events for copying/pasting/deleting
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Exit if not selected or if editing text
      if (!isSelected || isEditing) return;
      
      // Don't process keyboard events if focus is on an input element
      if (document.activeElement && 
          (document.activeElement.tagName === 'INPUT' || 
           document.activeElement.tagName === 'TEXTAREA' ||
           document.activeElement.tagName === 'SELECT')) {
        return;
      }
      
      // Copy (Ctrl+C / Cmd+C)
      if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
        e.preventDefault();
        localStorage.setItem('clipboard-item', JSON.stringify({
          type: item.type,
          content: item.content,
          style: item.style,
          size: item.size,
          zIndex: item.zIndex
        }));
      }
      
      // Paste (Ctrl+V / Cmd+V)
      if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
        e.preventDefault();
        try {
          const clipboardData = localStorage.getItem('clipboard-item');
          if (clipboardData) {
            const parsedData = JSON.parse(clipboardData);
            addItem({
              ...parsedData,
              position: {
                x: item.position.x + 20,
                y: item.position.y + 20
              }
            });
          }
        } catch (error) {
          console.error('Failed to paste:', error);
        }
      }
      
      // Delete (Delete / Backspace)
      if ((e.key === 'Delete' || e.key === 'Backspace') && !isEditing) {
        e.preventDefault();
        deleteItem(item.id);
      }
    };
    
    if (isSelected) {
      window.addEventListener('keydown', handleKeyDown);
    }
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isSelected, item, addItem, deleteItem, isEditing]);

  // DRAG HANDLING
  const handleDrag = useCallback((e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (resizingRef.current) {
      handleResizeMove(e);
      return;
    }
    
    // Calculate the distance moved
    const dx = (e.clientX - startPosRef.current.mouseX) / scale;
    const dy = (e.clientY - startPosRef.current.mouseY) / scale;
    
    // Calculate new position
    let newX = startPosRef.current.itemX + dx;
    let newY = startPosRef.current.itemY + dy;
    
    // Ensure the item stays completely within the canvas bounds
    if (canvasWidth !== undefined && canvasHeight !== undefined) {
      // Limit x position to keep element inside canvas
      newX = Math.max(0, Math.min(newX, canvasWidth - item.size.width));
      
      // Limit y position to keep element inside canvas
      newY = Math.max(0, Math.min(newY, canvasHeight - item.size.height));
    }
    
    // Update item position
    updateItem(item.id, {
      position: {
        x: newX,
        y: newY
      }
    });
  }, [canvasHeight, canvasWidth, item.id, item.size.width, item.size.height, scale, updateItem]);
  
  const handleDragEnd = useCallback((e?: MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    resizingRef.current = null;
    window.removeEventListener('mousemove', handleDrag);
    window.removeEventListener('mouseup', handleDragEnd);
  }, [handleDrag]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (isEditing) return;
    
    e.stopPropagation();
    e.preventDefault();
    selectItem(item.id);
    
    // Save the starting mouse position and item position
    startPosRef.current = {
      mouseX: e.clientX,
      mouseY: e.clientY,
      itemX: item.position.x,
      itemY: item.position.y
    };
    
    window.addEventListener('mousemove', handleDrag);
    window.addEventListener('mouseup', handleDragEnd);
  }, [handleDrag, handleDragEnd, isEditing, item.id, item.position.x, item.position.y, selectItem]);

  const handleResizeStart = useCallback((e: React.MouseEvent, direction: ResizeDirection) => {
    e.stopPropagation();
    e.preventDefault();
    
    selectItem(item.id);
    resizingRef.current = direction;
    
    // Save the starting mouse position, item position and size
    startPosRef.current = {
      mouseX: e.clientX,
      mouseY: e.clientY,
      itemX: item.position.x,
      itemY: item.position.y
    };
    
    startSizeRef.current = {
      width: item.size.width,
      height: item.size.height,
      x: item.position.x,
      y: item.position.y
    };
    
    window.addEventListener('mousemove', handleDrag);
    window.addEventListener('mouseup', handleDragEnd);
  }, [handleDrag, handleDragEnd, item.id, item.position.x, item.position.y, item.size.height, item.size.width, selectItem]);

  const handleResizeMove = useCallback((e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!resizingRef.current) {
      console.log('No resizing direction set');
      return;
    }
    
    const direction = resizingRef.current;
    console.log('Resizing in direction:', direction);
    
    // Calculate the distance moved
    const dx = (e.clientX - startPosRef.current.mouseX) / scale;
    const dy = (e.clientY - startPosRef.current.mouseY) / scale;
    
    console.log('Movement delta:', dx, dy);
    
    let newWidth = startSizeRef.current.width;
    let newHeight = startSizeRef.current.height;
    let newX = startSizeRef.current.x;
    let newY = startSizeRef.current.y;
    
    // Update size based on direction
    if (direction.includes('e')) {
      newWidth = Math.max(30, startSizeRef.current.width + dx);
    } else if (direction.includes('w')) {
      const widthChange = Math.min(startSizeRef.current.width - 30, dx);
      newWidth = startSizeRef.current.width - widthChange;
      newX = startSizeRef.current.x + widthChange;
    }
    
    if (direction.includes('s')) {
      newHeight = Math.max(30, startSizeRef.current.height + dy);
    } else if (direction.includes('n')) {
      const heightChange = Math.min(startSizeRef.current.height - 30, dy);
      newHeight = startSizeRef.current.height - heightChange;
      newY = startSizeRef.current.y + heightChange;
    }
    
    // Ensure the item stays completely within the canvas bounds
    if (canvasWidth !== undefined && canvasHeight !== undefined) {
      // Check if resizing would push the element outside the canvas
      if (newX < 0) {
        // If moving left edge would push outside, adjust width and keep x at 0
        newWidth = newWidth + newX;
        newX = 0;
      }
      
      if (newY < 0) {
        // If moving top edge would push outside, adjust height and keep y at 0
        newHeight = newHeight + newY;
        newY = 0;
      }
      
      // If right edge is beyond canvas, limit width
      if (newX + newWidth > canvasWidth) {
        newWidth = canvasWidth - newX;
      }
      
      // If bottom edge is beyond canvas, limit height
      if (newY + newHeight > canvasHeight) {
        newHeight = canvasHeight - newY;
      }
      
      // Ensure minimum dimensions
      newWidth = Math.max(30, newWidth);
      newHeight = Math.max(30, newHeight);
    }
    
    console.log('New size:', newWidth, newHeight, 'New position:', newX, newY);
    
    // Update the item
    updateItem(item.id, {
      position: { x: newX, y: newY },
      size: { width: newWidth, height: newHeight }
    });
  }, [canvasHeight, canvasWidth, item.id, scale, updateItem]);

  const handleDoubleClick = useCallback((e: React.MouseEvent) => {
    if (item.type === 'text') {
      e.stopPropagation();
      setEditText(item.content);
      setIsEditing(true);
    }
  }, [item.content, item.type]);

  // Lắng nghe sự kiện resizeStart từ component ResizeHandles
  useEffect(() => {
    if (itemRef.current && isSelected) {
      const handleResizeEvent = (e: Event) => {
        const customEvent = e as CustomEvent;
        console.log('Received resizeStart event', customEvent.detail);
        
        if (customEvent.detail?.itemId === item.id) {
          // Gọi trực tiếp để đảm bảo context đúng
          resizingRef.current = customEvent.detail.direction;
          
          // Lưu vị trí ban đầu của chuột và kích thước item
          const mouseEvent = customEvent.detail.originalEvent as MouseEvent;
          startPosRef.current = {
            mouseX: mouseEvent.clientX,
            mouseY: mouseEvent.clientY,
            itemX: item.position.x,
            itemY: item.position.y
          };
          
          startSizeRef.current = {
            width: item.size.width,
            height: item.size.height,
            x: item.position.x,
            y: item.position.y
          };
          
          // Thêm event listeners cho việc di chuyển
          window.addEventListener('mousemove', handleDrag);
          window.addEventListener('mouseup', handleDragEnd);
          
          console.log('Resize started:', customEvent.detail.direction);
        }
      };
      
      // Đảm bảo sự kiện được đăng ký đúng
      itemRef.current.addEventListener('resizeStart', handleResizeEvent);
      
      return () => {
        if (itemRef.current) {
          itemRef.current.removeEventListener('resizeStart', handleResizeEvent);
        }
      };
    }
  }, [item.id, item.position.x, item.position.y, item.size.width, item.size.height, itemRef, isSelected, handleDrag, handleDragEnd]);

  // Thêm kiểm tra giới hạn cho vị trí hiện tại
  useEffect(() => {
    if (canvasWidth && canvasHeight) {
      // Nếu vị trí hiện tại nằm ngoài giới hạn canvas, di chuyển vào trong
      let needsUpdate = false;
      let newX = item.position.x;
      let newY = item.position.y;

      // Giới hạn trên và trái
      if (newX < 0) {
        newX = 0;
        needsUpdate = true;
      }
      
      if (newY < 0) {
        newY = 0;
        needsUpdate = true;
      }
      
      // Giới hạn phải và dưới
      if (newX + item.size.width > canvasWidth) {
        newX = canvasWidth - item.size.width;
        needsUpdate = true;
      }
      
      if (newY + item.size.height > canvasHeight) {
        newY = canvasHeight - item.size.height;
        needsUpdate = true;
      }
      
      // Cập nhật nếu cần
      if (needsUpdate) {
        updateItem(item.id, {
          position: { x: newX, y: newY }
        });
      }
    }
  }, [item, canvasWidth, canvasHeight, updateItem]);

  return {
    itemStyle,
    isEditing,
    setIsEditing,
    editText,
    setEditText,
    handleMouseDown,
    handleDoubleClick,
    handleResizeStart,
  };
} 